import {
    Observable,
    map
} from "rxjs";

import {
    Injectable, inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    GetResponse,
    RequestUtil,
    SearchRequest,
    SearchResponse
} from "@shopliftr/common-js/shared";

import {
    InternalApiRequestService,
    IReadOnlyInternalApiClientService,
    IQueryInternalApiClientService,
    ListUtil
} from "@shopliftr/common-ng";

import {
    ModelAction, ISearchSort, SearchSortDirection
} from "@shopliftr/common-js/data";
import {
    ByodProduct, ByodProductSearchFilterOptions
} from "@shopliftr/common-js/smart";

/**
 * Service for handling calls to InternalApi relating to ByodProduct.
 *
 * This service implements the **Repository Pattern**, providing a clean abstraction layer
 * between the application's business logic and the data access layer. It encapsulates
 * all CRUD operations and search functionality for ByodProduct entities, delegating the
 * actual HTTP communication to the InternalApiRequestService.
 *
 * @pattern Repository Pattern
 * @implements {IReadOnlyInternalApiClientService<ByodProduct>}
 * @implements {IQueryInternalApiClientService<ByodProduct>}
 */
@Injectable({ providedIn: "root" })
export class ByodProductApiClientService implements IReadOnlyInternalApiClientService<ByodProduct>,
IQueryInternalApiClientService<ByodProduct> {
    /**
     * Base API path for all ByodProduct-related endpoints.
     * Used as the root path segment for constructing API URLs.
     *
     * @readonly
     */
    readonly basePath = "byod-product";

    /**
     * Injected service responsible for making HTTP requests to the Internal API.
     * Handles the low-level communication details including request formatting,
     * error handling, and response parsing.
     *
     * @private
     * @readonly
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieves multiple ByodProduct entities by their unique identifiers in a single bulk request.
     *
     * @param ids - Array of unique identifiers of the ByodProduct entities to retrieve
     * @returns Observable that emits a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ByodProduct>(id, ByodProduct.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single ByodProduct entity by its unique identifier.
     *
     * @param id - The unique identifier of the ByodProduct to retrieve
     * @returns Observable that emits the requested ByodProduct entity
     * @example
     * this.byodProductService.get('product-123').subscribe(product => {
     *   console.log(product.name);
     * });
     */
    get$(id: string): Observable<ByodProduct> {

        const request = RequestUtil.createGetRequest<void>(id, ByodProduct.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<ByodProduct>(request, this.basePath).pipe(map((response: GetResponse<ByodProduct>) => {

            return response.payload;
        }));
    }

    /**
     * Retrieves all ByodProduct entities matching the specified filter criteria.
     * Results are returned as a complete array without pagination.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [sort] - Optional sorting configuration. Defaults to ascending sort by name
     * @returns Observable that emits an array of all matching ByodProduct entities
     * @example
     * this.byodProductService.getAll({ campaignId: 'campaign-123' }).subscribe(products => {
     *   console.log(`Found ${products.length} products`);
     * });
     */
    getAll$(filterOptions: ByodProductSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ByodProduct>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, ByodProduct.modelName, undefined, undefined, filterOptions, sort ?? [{
                field: "name",
                direction: SearchSortDirection.Ascending
            }]
        );

        return this._internalApiRequestService.getAll$<ByodProduct, ByodProductSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Performs a paginated search for ByodProduct entities matching the specified criteria.
     * Returns results with pagination metadata for efficient data loading.
     *
     * @param filterOptions - Filter criteria to apply to the search
     * @param [page] - Zero-based page number for pagination
     * @param [pageSize] - Number of results per page
     * @param [sort] - Optional sorting configuration
     * @returns Observable that emits a SearchResponse containing matching entities and pagination metadata
     * @example
     * this.byodProductService.search({ campaignId: 'campaign-123' }, 0, 20).subscribe(response => {
     *   console.log(`Page ${response.page} of ${response.totalPages}`);
     *   console.log(response.results);
     * });
     */
    search$(
        filterOptions: ByodProductSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ByodProduct>> {

        const request: SearchRequest<ByodProductSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodProduct.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$<ByodProduct, ByodProductSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Searches for ByodProduct entities by their UPC codes.
     * Automatically constructs filter options and retrieves products matching the provided UPCs.
     *
     * @param upcs - Array of UPC codes to search for
     * @param campaignId - Campaign identifier to scope the search
     * @returns Observable that emits a SearchResponse containing matching products sorted by name
     * @example
     * this.byodProductService.searchProductsByUpcs(['123456', '789012'], 'campaign-123').subscribe(response => {
     *   console.log(`Found ${response.total} products`);
     *   response.results.forEach(product => console.log(product.name));
     * });
     */
    searchProductsByUpcs$(upcs: Array<string>, campaignId: string): Observable<SearchResponse<ByodProduct>> {

        const filterOptions = new ByodProductSearchFilterOptions();
        filterOptions.upcs = upcs;
        filterOptions.campaignId = campaignId;

        const request: SearchRequest<ByodProductSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ByodProduct.modelName,
            0,
            upcs.length,
            filterOptions,
            [
                {
                    field: "name",
                    direction: SearchSortDirection.Ascending
                }
            ]
        );

        return this._internalApiRequestService.search$<ByodProduct, ByodProductSearchFilterOptions>(request, this.basePath);
    }
}
